/**
 * Plan-limit error parsing.
 *
 * Deliberately dependency-free — no imports at all — so it can be unit tested
 * directly, and so any caller can read a limit error without pulling in the
 * axios client.
 */

export type PlanTier = 'trial' | 'yoluno' | 'family' | 'family_plus';

/** Machine-readable reasons a 402 can carry (see server/services/entitlements.ts). */
export type LimitCode =
  | 'child_profile_limit'
  | 'story_limit'
  | 'voice_limit'
  | 'fair_use_messages';

export interface PlanLimitError {
  code: LimitCode;
  limit: number;
  used: number;
  tier: PlanTier;
  /** The server's sentence, already written for a parent to read. */
  message: string;
}

/**
 * Find the HTTP response on a thrown value.
 *
 * Services that go through `lib/api` throw a NORMALIZED error object and keep
 * the axios error under `originalError`, so reading `err.response` directly
 * finds nothing. Walk the chain instead.
 */
function findResponse(err: unknown): { status?: number; data?: unknown } | null {
  let current: unknown = err;
  for (let i = 0; i < 5 && current; i++) {
    const response = (current as { response?: { status?: number; data?: unknown } }).response;
    if (response && typeof response === 'object') return response;
    const inner = (current as { originalError?: unknown }).originalError;
    if (!inner || inner === current) return null;
    current = inner;
  }
  return null;
}

/**
 * Read a plan-limit error, or null if this isn't one.
 *
 * A 402 is either the hard paywall (`error: 'subscription_required'`, no code)
 * or a plan limit (carries a code plus the numbers). They need different UI —
 * one sends you to checkout, the other names what ran out — so only the latter
 * matches here.
 */
export function limitFromError(err: unknown): PlanLimitError | null {
  const response = findResponse(err);
  if (!response || response.status !== 402) return null;

  const data = (response.data ?? {}) as Record<string, unknown>;
  if (typeof data.code !== 'string') return null;

  return {
    code: data.code as LimitCode,
    limit: Number(data.limit ?? 0),
    used: Number(data.used ?? 0),
    tier: (typeof data.tier === 'string' ? data.tier : 'yoluno') as PlanTier,
    message:
      typeof data.error === 'string' && data.error
        ? data.error
        : 'You have reached a plan limit.',
  };
}
